// Angular core dependencies
import { NgModule } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { ScrollingModule } from '@angular/cdk/scrolling';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { DragDropModule } from '@angular/cdk/drag-drop';
import { MatRadioModule } from '@angular/material/radio';
import { MatInputModule } from '@angular/material/input';
import { MatDialogModule } from '@angular/material/dialog';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatSelectModule } from '@angular/material/select';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatBottomSheetModule } from '@angular/material/bottom-sheet';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { MatFormFieldModule } from '@angular/material/form-field';

// Third parties dependencies
import { Observable } from 'rxjs';
import { catchError } from 'rxjs/operators';

import { NzDatePickerModule } from 'ng-zorro-antd/date-picker';
import { NzCarouselModule } from 'ng-zorro-antd/carousel';
import { NzMessageModule } from 'ng-zorro-antd/message';
import { NzUploadModule } from 'ng-zorro-antd/upload';
import { NzListModule } from 'ng-zorro-antd/list';
import { NzTagModule } from 'ng-zorro-antd/tag';
import { NzFormModule } from 'ng-zorro-antd/form';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzTimePickerModule } from 'ng-zorro-antd/time-picker';
import { NzRadioModule } from 'ng-zorro-antd/radio';
import { NzCollapseModule } from 'ng-zorro-antd/collapse';
import { NzCheckboxModule } from 'ng-zorro-antd/checkbox';
import { NzBadgeModule } from 'ng-zorro-antd/badge';
import { NzToolTipModule } from 'ng-zorro-antd/tooltip';
import { NzSwitchModule } from 'ng-zorro-antd/switch';
import { NzDropDownModule } from 'ng-zorro-antd/dropdown';
import { NzTableModule } from 'ng-zorro-antd/table';
import { NzDividerModule } from 'ng-zorro-antd/divider';
import { NzProgressModule } from 'ng-zorro-antd/progress';
import { NzEmptyModule } from 'ng-zorro-antd/empty';
import { NzSkeletonModule } from 'ng-zorro-antd/skeleton';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { NzTabsModule } from 'ng-zorro-antd/tabs';
import { NzPopoverModule } from 'ng-zorro-antd/popover';
import { NzInputNumberModule } from 'ng-zorro-antd/input-number';
import { NzTypographyModule } from 'ng-zorro-antd/typography';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzPaginationModule } from 'ng-zorro-antd/pagination';
import { NzResultModule } from 'ng-zorro-antd/result';
import { NzGridModule } from 'ng-zorro-antd/grid';
import { NzDrawerModule } from 'ng-zorro-antd/drawer';
import { NzSliderModule } from 'ng-zorro-antd/slider';
import { NzModalModule } from 'ng-zorro-antd/modal';
import { NzLayoutModule } from 'ng-zorro-antd/layout';
import { NzStepsModule } from 'ng-zorro-antd/steps';
import { NzStatisticModule } from 'ng-zorro-antd/statistic';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzCardModule } from 'ng-zorro-antd/card';
import { NZ_I18N, en_US } from 'ng-zorro-antd/i18n';
import { TranslateModule, TranslateLoader } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { NgMultiSelectDropDownModule } from 'ng-multiselect-dropdown';
import { LottieModule } from 'ngx-lottie';
import { NgxQRCodeModule } from '@techiediaries/ngx-qrcode';
import { NgOtpInputModule } from 'ng-otp-input';

// Directives
import { ScrollerDirective } from '../common/directives/scroller.directive';
import { onlyNumbersDirective } from '../common/directives/onlyNumbers.directive';
import { NumberFormatDirective } from '../common/directives/number-format.directive';
import { AlphanumericOnlyDirective } from './directives/alpha-numeric.directive';

// Pipes
import { FallbackTranslatePipe } from '../common/fallback.pipe';
import { TimezonePipe } from '../common/pipes/timezone.pipe';
import { KeysPipe } from '../common/components/custom-otp-input/keys.pipe';
import { TruncatePipe } from '../common/pipes/truncate.pipe';
import { RemoveNegativeSignPipe } from '../common/pipes/remove-negative-sign.pipe';
import { FcDiscountComponent } from '../common/components/fc-discount/fc-discount.component';
import { DeleteDiscountComponent } from '../common/components/delete-discount/delete-discount.component';
import { SelectRestaurantComponent } from '../common/components/select-restaurant/select-restaurant.component';
import { CurrencyValPipe } from '../util/currency-val.pipe';

// Services
import { TableBookingApiService } from '../table-bookings/services/table-bookings-api.service';
import { TableBookingModalService } from '../table-bookings/services/table-bookings-modal.service';
import { TableBookingHelperService } from '../table-bookings/services/table-bookings-helper.service';
import { CollectionInfrastructureHelperService } from '../collection-infrastructure/collection-infrastructure-helper.service';
import { WarningBannerComponent } from '../common/components/warning-banner/warning-banner.component';
import { CommonNudgeComponent } from '../common/nudge/common-nudge/common-nudge.component';
import { InfraNudgeComponent } from '../collection-infrastructure/nudges/infra-nudge/infra-nudge.component';
import { HqRestaurantsDueModalComponent } from '../collection-infrastructure/modals/hq-restaurants-due-modal/hq-restaurants-due-modal.component';
import { PaymentMethodModalComponent } from '../collection-infrastructure/modals/payment-method-modal/payment-method-modal.component';
import { LockedFeatureComponent } from '../collection-infrastructure/modals/locked-feature-modal/locked-feature.component';
import {PromiseLockModalComponent} from '../collection-infrastructure/modals/promise-lock-modal/promise-lock-modal.component'

// Modal Component
import { CommonModalComponent } from '../common/dialog/common-modal/common-modal.component';
import { AddDiscountComponent } from '../common/components/add-discount/add-discount.component';
import { AddItemDiscountComponent } from '../common/components/add-item-discount/add-item-discount.component';
import { AddTopupComponent } from '../common/components/add-topup/add-topup.component';
import { DeleteCreatedOrderComponent } from '../common/components/delete-created-order/delete-created-order.component';
import { RevertOrderComponent } from '../common/components/revert-order/revert-order.component';
import { EmailInvoiceComponent } from './shared modal/email-invoice/email-invoice.component';
import { VoucherTitleComponent } from './shared modal/voucher-title/voucher-title.component';
import { RedeemLoyaltyComponent } from './shared modal/redeem-loyalty/redeem-loyalty.component';
import { TalkExpertFormComponent } from './shared modal/talk-expert-form/talk-expert-form.component';
import { LockedPosComponent } from './shared modal/locked-pos/locked-pos.component';
import { MposFilterComponent } from './shared modal/mpos-filter/mpos-filter.component';
import { LockedLiteComponent } from './shared modal/locked-lite/locked-lite.component';
import { LockedPosAfterPromiseComponent } from './shared modal/locked-pos-after-promise/locked-pos-after-promise.component';
import { ReoccurKnowMoreComponent } from './shared modal/reoccur-know-more/reoccur-know-more.component';
import { WaitingPaymentComponent } from './components/waiting-payment/waiting-payment.component';
import { CancelPaymentComponent } from './components/cancel-payment/cancel-payment.component';
import { PaymentStatusPopupComponent } from './components/payment-status-popup/payment-status-popup.component';
import { StaffGuidePopupComponent } from './components/staff-guide-popup/staff-guide-popup.component';
import { TimeOutPopupComponent } from './components/time-out-popup/time-out-popup.component';
import { ProgressBarComponent } from './components/progress-bar/progress-bar.component';
import { IframeComponent } from '../pos-order/modals/iframe/iframe.component';
import { ViewBillComponent } from '../pos-order/modals/view-bill/view-bill.component';
import { CartModalComponent } from '../pos-order/modals/cart-modal/cart-modal.component';
import { OrderTypeComponent } from '../pos-order/modals/order-type/order-type.component';
import { PaymentQRComponent } from '../pos-order/modals/payment-qr/payment-qr.component';
import { ChooseOrdertypeComponent } from '../pos-mobile/modals/choose-ordertype/choose-ordertype.component';
import { CustomTimingsComponent } from '../invoice/modals/custom-timings/custom-timings.component';
import { UpdateCashierComponent } from '../invoice/modals/update-cashier/update-cashier.component';
import { CashTallyComponent } from '../invoice/modals/cash-tally/cash-tally.component';
import { CompleteOrderPromptComponent } from '../order/modal/complete-order-prompt/complete-order-prompt.component';
import { AllPrinterModalComponent } from '../features/printer-status/components/all-printer-modal/all-printer-modal.component';
import { NoDelayEventsModalComponent } from '../features/printer-status/components/no-delay-events-modal/no-delay-events-modal.component';

// Components
import { components } from '../common/components';
import { HeaderComponent } from '../common/header/header.component';
import { VirtualKeyboardComponent } from '../common/components/virtual-keyboard/virtual-keyboard.component';
import { ChooseOutletComponent } from '../common/components/choose-outlet/choose-outlet.component';
import { ComparisionStatsComponent } from '../common/components/comparision-stats/comparision-stats.component';
import { SelectCheckAllComponent } from '../common/components/choose-outlet/select-check-all.component';
import { OtpInputComponent } from '../common/components/custom-otp-input/custom-otp-input.component';
import { LoadingSpinnerComponent } from '../common/loading-spinner/loading-spinner.component';
import { SearchboxComponent } from '../common/components/searchbox/searchbox.component';
import { ImageEditorModule } from '../common/components/image-editor/image-editor.module';
import { NoOutletsComponent } from '../common/no-outlets/no-outlets.component';
import { PaymentBannerComponent } from '../common/components/payment-banner/payment-banner.component';
import { PaymentRequestComponent } from '../payment-request/payment-request.component';
import { DishImageUploadComponent } from '../modals/dish-image-upload/dish-image-upload.component';
import { LanguageChangerComponent } from './components/language-changer/language-changer.component';
import { PrinterStatusWidgetComponent } from '../features/printer-status/components/printer-status-widget/printer-status-widget.component';
import { MposFilterButtonComponent } from './components/mpos-filter-button/mpos-filter-button.component';
import { DelayPrintingComponent } from '../features/printer-status/components/delay-printing/delay-printing.component';
import { CollectionInfrastructureService } from '../collection-infrastructure/collection-infrastructure.service';
import { BillingCounterSetupComponent } from './shared modal/billing-counter-setup/billing-counter-setup.component';
import { PaymentHistoryComponent } from '../order/modal/payment-history/payment-history.component';
import { InvoiceCardComponent } from '../invoice/components/invoice-card/invoice-card.component';
import { MultiselectDropdownComponent } from './components/multiselect-dropdown/multiselect-dropdown.component';
import { NoDataComponent } from 'src/app/invoice/components/no-data/no-data.component';
import { SettlementModalService } from '../settlement/services/settlement-modal.service';

export function playerFactory() {
  return import(/* webpackChunkName: 'lottie-web' */ 'lottie-web');
}

export class CustomTranslateHttpLoader extends TranslateHttpLoader {
  constructor(http: HttpClient, prefix: string, suffix: string) {
    super(http, prefix, suffix);
  }

  getTranslation(lang: string): Observable<any> {
    return super.getTranslation(lang).pipe(
      catchError((error: any) => {
        console.error('Translation file loading error:', error);
        const fallbackLang = 'en_US'; // Set a fallback language code (e.g., 'en' for English)
        return import(`../../assets/locale/shared/${fallbackLang}.json`);
      })
    );
  }
}

export function SharedHttpLoaderFactory(http: HttpClient) {
  return new CustomTranslateHttpLoader(
    http,
    './assets/locale/shared/',
    '.json'
  );
}

@NgModule({
  declarations: [
    /*Directives*/
    ScrollerDirective,
    FallbackTranslatePipe,
    TimezonePipe,
    TruncatePipe,
    ViewBillComponent,
    AlphanumericOnlyDirective,
    /*Components*/
    ...components,
    HeaderComponent,
    PaymentBannerComponent,
    WarningBannerComponent,
    PaymentRequestComponent,
    LoadingSpinnerComponent,
    DishImageUploadComponent,
    AddDiscountComponent,
    AddTopupComponent,
    SelectRestaurantComponent,
    DeleteDiscountComponent,
    FcDiscountComponent,
    AddItemDiscountComponent,
    ChooseOutletComponent,
    HqRestaurantsDueModalComponent,
    PaymentMethodModalComponent,
    LockedFeatureComponent,
    SelectCheckAllComponent,
    ComparisionStatsComponent,
    KeysPipe,
    VirtualKeyboardComponent,
    CurrencyValPipe,
    RemoveNegativeSignPipe,
    onlyNumbersDirective,
    OtpInputComponent,
    EmailInvoiceComponent,
    VoucherTitleComponent,
    RedeemLoyaltyComponent,
    UpdateCashierComponent,
    ChooseOrdertypeComponent,
    CartModalComponent,
    OrderTypeComponent,
    CashTallyComponent,
    SearchboxComponent,
    CompleteOrderPromptComponent,
    LanguageChangerComponent,
    PrinterStatusWidgetComponent,
    AllPrinterModalComponent,
    NoDelayEventsModalComponent,
    NoOutletsComponent,
    IframeComponent,
    WaitingPaymentComponent,
    CancelPaymentComponent,
    PaymentStatusPopupComponent,
    DelayPrintingComponent,
    TimeOutPopupComponent,
    StaffGuidePopupComponent,
    RevertOrderComponent,
    DeleteCreatedOrderComponent,
    PaymentQRComponent,
    TalkExpertFormComponent,
    LockedPosComponent,
    LockedPosAfterPromiseComponent,
    CustomTimingsComponent,
    ReoccurKnowMoreComponent,
    NumberFormatDirective,
    ProgressBarComponent,
    CommonModalComponent,
    LockedLiteComponent,
    CommonNudgeComponent,
    InfraNudgeComponent,
    PromiseLockModalComponent,
    MposFilterComponent,
    MposFilterButtonComponent,
    BillingCounterSetupComponent,
    PaymentHistoryComponent,
    InvoiceCardComponent,
    MultiselectDropdownComponent,
    NoDataComponent
  ],
  imports: [
    TranslateModule.forChild({
      loader: {
        provide: TranslateLoader,
        useFactory: SharedHttpLoaderFactory,
        deps: [HttpClient],
      },
      isolate: true,
    }),
    CommonModule,
    NgMultiSelectDropDownModule.forRoot(),
    NzDatePickerModule,
    NzSelectModule,
    NzMessageModule,
    NzCarouselModule,
    NzUploadModule,
    NzTagModule,
    NzListModule,
    NzInputModule,
    NzFormModule,
    NzStatisticModule,
    LottieModule.forRoot({ player: playerFactory }),
    NzSliderModule,
    NzTimePickerModule,
    FormsModule,
    ReactiveFormsModule,
    MatCheckboxModule,
    MatSelectModule,
    MatSlideToggleModule,
    MatRadioModule,
    DragDropModule,
    NzCollapseModule,
    MatBottomSheetModule,
    NzToolTipModule,
    NzSwitchModule,
    NzDropDownModule,
    NzTableModule,
    NzDividerModule,
    NzProgressModule,
    NzEmptyModule,
    NzSkeletonModule,
    NzBadgeModule,
    NzSpinModule,
    NzTabsModule,
    NzPopoverModule,
    NzButtonModule,
    NzRadioModule,
    NzStepsModule,
    NzGridModule,
    NzModalModule,
    NzLayoutModule,
    NzCheckboxModule,
    ImageEditorModule,
    ScrollingModule,
    NzIconModule,
    NzCardModule
  ],
  exports: [
    LottieModule,
    TimezonePipe,
    FallbackTranslatePipe,
    TruncatePipe,
    CommonModule,
    NgMultiSelectDropDownModule,
    NzDatePickerModule,
    NzMessageModule,
    NzCarouselModule,
    NzUploadModule,
    NzTagModule,
    NzListModule,
    NzSliderModule,
    MatCheckboxModule,
    MatSelectModule,
    MatSlideToggleModule,
    MatRadioModule,
    NzFormModule,
    NzInputModule,
    NzSelectModule,
    FormsModule,
    ReactiveFormsModule,
    DragDropModule,
    NzIconModule,
    NzRadioModule,
    NzCollapseModule,
    VirtualKeyboardComponent,
    ...components,
    HeaderComponent,
    PaymentBannerComponent,
    WarningBannerComponent,
    PaymentRequestComponent,
    LoadingSpinnerComponent,
    MatBottomSheetModule,
    AddDiscountComponent,
    AddTopupComponent,
    SelectRestaurantComponent,
    DeleteDiscountComponent,
    FcDiscountComponent,
    AddItemDiscountComponent,
    SearchboxComponent,
    DishImageUploadComponent,
    NzCheckboxModule,
    MatExpansionModule,
    MatSnackBarModule,
    NzBadgeModule,
    NzToolTipModule,
    NzSwitchModule,
    NzDropDownModule,
    NzTableModule,
    NzDividerModule,
    NzProgressModule,
    NzEmptyModule,
    NgOtpInputModule,
    NzSkeletonModule,
    NzTimePickerModule,
    NzBadgeModule,
    NzSpinModule,
    NzTabsModule,
    NzPopoverModule,
    NzInputNumberModule,
    NzTypographyModule,
    NzButtonModule,
    NzPaginationModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    NzResultModule,
    ChooseOutletComponent,
    HqRestaurantsDueModalComponent,
    PaymentMethodModalComponent,
    LockedFeatureComponent,
    SelectCheckAllComponent,
    ComparisionStatsComponent,
    NzGridModule,
    OtpInputComponent,
    NgxQRCodeModule,
    NzDrawerModule,
    NzRadioModule,
    NzStepsModule,
    ChooseOrdertypeComponent,
    CartModalComponent,
    OrderTypeComponent,
    LanguageChangerComponent,
    /*Directives*/
    ScrollerDirective,
    NzLayoutModule,
    NzStatisticModule,
    CurrencyValPipe,
    RemoveNegativeSignPipe,
    LanguageChangerComponent,
    PrinterStatusWidgetComponent,
    NoOutletsComponent,
    IframeComponent,
    WaitingPaymentComponent,
    CancelPaymentComponent,
    PaymentStatusPopupComponent,
    StaffGuidePopupComponent,
    TimeOutPopupComponent,
    PaymentQRComponent,
    NumberFormatDirective,
    AlphanumericOnlyDirective,
    ProgressBarComponent,
    CommonModalComponent,
    CommonNudgeComponent,
    InfraNudgeComponent,
    LockedLiteComponent,
    MposFilterComponent,
    MposFilterButtonComponent,
    PaymentHistoryComponent,
    NzModalModule,
    InvoiceCardComponent,
    ScrollingModule,
    MultiselectDropdownComponent,
    NoDataComponent,
    MultiselectDropdownComponent,
    NzCardModule
  ],
  providers: [
    { provide: NZ_I18N, useValue: en_US },
    DatePipe,
    KeysPipe,
    FallbackTranslatePipe,
    TableBookingApiService,
    TableBookingModalService,
    TableBookingHelperService,
    CollectionInfrastructureHelperService,
    CollectionInfrastructureService,
    SettlementModalService
  ],
})
export class SharedModule {}
